<template>
  <div class="register-candidate-form">
    <div class="register-candidate-form__header-block">
      <h1 class="register-candidate-form__header">
        <span>{{ $tCandidateForm('Register candidate') }}</span>
        <BaseSvg name="ico-check-rectangular" width="2.8rem" height="2.1rem" />
      </h1>
      <div class="register-candidate-form__header-block-subheader">{{ description }}</div>
    </div>

    <RegisterCandidateFormBlock :header="$tCandidateForm('Personal data')">
      <div class="register-candidate-form__row-inputs">
        <BaseInput
          ref="lastName"
          v-model="formData.lastName"
          :label="$tCandidateForm('Surname')"
          :error="validationErrors.lastName"
        />
        <BaseInput
          ref="firstName"
          v-model="formData.firstName"
          :label="$tCandidateForm('Name')"
          :error="validationErrors.firstName"
        />
      </div>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock
      ref="desiredCountries"
      :header="$tCandidateForm('Country you wanna work')"
      :subheader="$tCandidateForm('You can choose few')"
    >
      <BaseCheckboxesList
        v-model="formData.desiredCountries"
        :list="dataLists.register_candidate_desired_countries"
        label-key="name"
        class="register-candidate-form__checkboxes-list"
      />
      <BaseInputError :value="validationErrors.desiredCountries" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock
      ref="genderOrPair"
      :header="$tCandidateForm('Looking work for')"
    >
      <BaseRadioList
        v-model="formData.genderOrPair"
        vertical
        text-key="name"
        :options="dataLists.register_candidate_gender_or_pair"
      />
      <BaseInputError :value="validationErrors.genderOrPair" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('How old are you')">
      <div class="register-candidate-form__age-content">
        <BaseInput ref="age" v-model="formData.age" type="number" :error="validationErrors.age" />
      </div>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('Which type of work you are looking for')">
      <BaseRadioList
        ref="workType"
        v-model="formData.workType"
        vertical
        text-key="name"
        :options="dataLists.register_candidate_work_types"
      />
      <BaseInputError :value="validationErrors.workType" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock
      ref="desiredCategories"
      :header="$tCandidateForm('What field do you want to work in')"
      :subheader="$tCandidateForm('You can choose few')"
    >
      <BaseCheckboxesList
        v-model="formData.desiredCategories"
        :list="dataLists.register_candidate_work_categories"
        label-key="name"
        class="register-candidate-form__checkboxes-list"
      />
      <BaseInputError :value="validationErrors.desiredCategories" />

      <BaseInput
        ref="anotherDesiredCategory"
        v-if="anotherDesiredCategorySelected"
        v-model="formData.anotherDesiredCategory"
        :value="validationErrors.anotherDesiredCategory"
        class="mt-3"
      />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('Your last work experience')">
      <div
        v-for="(experience, idx) in formData.workExperience"
        :key="idx"
        class="register-candidate-form__last-work-experience"
      >
        <SProfession
          v-model="experience.specialization"
          :label="$tCandidateForm('Specialization')"
          :placeholder="$tCandidateForm('Start typing the name') + '...'"
          url="/api/vacancies-list-v2/categories-list"
        />
        <BaseSelect
          v-model="experience.term"
          :label="$tCandidateForm('Term of the work')"
          text-key="name"
          cancelable
          :options="dataLists.register_candidate_last_experience_terms"
        />

        <button
          v-if="formData.workExperience.length > 1"
          class="base-btn register-candidate-form__remove-block-button"
          @click="deleteFormWorkExperience(idx)"
        >
          <BaseSvg name="ico-cross" width="1.2rem" height="1.2rem" />
        </button>
      </div>

      <button
        class="base-btn base-btn--small register-candidate-form__add-button"
        @click="addMoreWorkExperience"
      >
        <BaseSvg name="ico-plus" width="1.2rem" height="1.2rem" />
        <span>{{ $tCandidateForm('Add experience') }}</span>
      </button>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('What are the certificates and diplomas')">
      <div class="register-candidate-form__certificates-and-diplomas">
        <BaseRadioList
          v-model="formData.documents.showed"
          text-key="name"
          :options="dataLists.register_candidate_certificates_and_diplomas"
          :class="{ 'mb-6': haveCertificateDocuments }"
        />

        <template v-if="haveCertificateDocuments">
          <div
            v-for="(file, index) in formData.documents.files"
            :key="index"
            class="d-flex register-candidate-form__inset-block"
          >
            <button
              v-if="formData.documents.files.length > 1"
              class="base-btn register-candidate-form__remove-block-button"
              @click="deleteFormDocument(index)"
            >
              <BaseSvg name="ico-cross" width="1.2rem" height="1.2rem" />
            </button>

            <div>
              <BaseSelect
                v-model="file.type"
                :label="$tCandidateForm('Document type')"
                :placeholder="$tCandidateForm('Start typing the name') + '...'"
                text-key="name"
                cancelable
                :options="dataLists.register_candidate_certificates_documents_types"
              />
              <div>
                <BaseInput
                  v-model="file.name"
                  :label="$tCandidateForm('Document name')"
                  text-key="name"
                />
              </div>
            </div>
            <div>
              <div class="register-candidate-form__document-upload-title">{{ $tCandidateForm('Upload') }}</div>
              <FilesUpload
                v-model="file.value"
                :max="1"
              />
            </div>
          </div>

          <div>
            <button
              class="base-btn base-btn--small register-candidate-form__add-button"
              @click="addFormDocument"
            >
              <BaseSvg name="ico-plus" width="1.2rem" height="1.2rem" />
              <span>{{ $tCandidateForm('Add document') }}</span>
            </button>
          </div>
        </template>
      </div>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('What is your passport')">
      <BaseRadioList
        ref="internationalPassport"
        v-model="formData.internationalPassport"
        vertical
        text-key="name"
        :options="dataLists.register_candidate_international_passport"
      />
      <BaseInputError :value="validationErrors.internationalPassport" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('Do you have a work visa?')">
      <BaseRadioList
        v-model="formData.workVisa.value"
        vertical
        text-key="name"
        :options="dataLists.register_candidate_work_visa"
      />

      <div v-if="haveWorkVisa">
        <SCountry
          v-model="formData.workVisa.country"
          :label="$tCandidateForm('Country for which visa is open')"
          class="mb-5 mt-6"
          url="/api/helper/countries-all"
        />

        <div class="pb-2">
          <div class="select__title">{{$tCandidateForm('Expiration date')}}</div>
          <BirthdaySelect
            v-model="formData.workVisa.expirationDate"
            :max-year="new Date().getFullYear() + 12"
          />
        </div>
      </div>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock
      ref="preferWorkBy"
      :header="$tCandidateForm('Do you want to work with a visa or biometric passport?')"
      :subheader="$tCandidateForm('You can choose few')"
    >
      <BaseCheckboxesList
        v-model="formData.preferWorkBy"
        :list="dataLists.register_candidate_wanna_work_by"
        label-key="name"
        class="register-candidate-form__checkboxes-list"
      />
      <BaseInputError :value="validationErrors.preferWorkBy" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock
      ref="preferWorkBodyPositions"
      :header="$tCandidateForm('We care about the health of those whom we help to find a job, so please choose, can you work?')"
      :subheader="$tCandidateForm('You can choose few')"
    >
      <BaseCheckboxesList
        v-model="formData.preferWorkBodyPositions"
        :list="dataLists.register_candidate_can_spend_workshift"
        label-key="name"
        class="register-candidate-form__checkboxes-list"
      />
      <BaseInputError :value="validationErrors.preferWorkBodyPositions" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock
      :header="$tCandidateForm('What is the most weight you can work with?')"
      :subheader="$tCandidateForm('choose the most important value for yourself')"
    >
      <BaseRadioList
        ref="maxWorkWeight"
        v-model="formData.maxWorkWeight"
        vertical
        text-key="name"
        :options="dataLists.register_candidate_max_work_weight"
      />
      <BaseInputError :value="validationErrors.maxWorkWeight" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('When are you ready to start the employment process?')">
      <BaseRadioList
        ref="readyToWork"
        v-model="formData.readyToWork"
        vertical
        text-key="name"
        :options="dataLists.register_candidate_ready_to_work"
      />
      <BaseInputError :value="validationErrors.readyToWork" />
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="$tCandidateForm('What language and level do you speak?')">
      <div class="register-candidate-form__languages-content">
        <div
          v-for="(language, index) in formData.languages"
          :key="index"
          class="register-candidate-form__inset-block"
        >
          <div>
            <SLanguage
              v-model="language.value"
              :label="$tCandidateForm('Language')"
              :list-filter="filterListLanguages"
            />
          </div>
          <div class="register-candidate-form__languages-content-experience">
            <BaseCheckbox
              v-for="item in dataLists.register_candidate_language_experience_levels"
              :key="item.id"
              :label="item.name"
              :checked="isCheckedInCheckboxesList(language.experience, item)"
              @input="bindWithCheckboxesList(language.experience, $event, item)"
            />
          </div>

          <button
            v-if="formData.languages.length > 1"
            class="base-btn register-candidate-form__remove-block-button"
            @click="deleteFormLanguage(index)"
          >
            <BaseSvg name="ico-cross" width="1.2rem" height="1.2rem" />
          </button>
        </div>

        <div>
          <button
            class="base-btn base-btn--small register-candidate-form__add-button"
            @click="addFormLanguage"
          >
            <BaseSvg name="ico-plus" width="1.2rem" height="1.2rem" />
            <span>{{$tCandidateForm('Add language')}}</span>
          </button>
        </div>
      </div>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock :header="customTitles.phone || $tCandidateForm('Enter your phone number')">
      <div class="register-candidate-form__phone-block-content">
        <BaseInputPhone
          ref="phone"
          v-model="formData.phone"
          :error="validationErrors.phone"
          @validate="onPhoneValidate"
        />
      </div>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock v-if="useAdditionalQuestions">
      <div slot="header" class="register-candidate-form__additional-questions-header">
        <div>
          <h2 class="mb-1">{{$tCandidateForm('Additional questions for the agent')}}</h2>
          <div class="register-candidate-form-block__subheader">{{ $tCandidateForm('not required') }}</div>
        </div>
        <div>
          <button
            class="base-btn base-btn--outline-grey"
            @click="show.additionalQuestions = !show.additionalQuestions"
          >
            <BaseSvg :name="show.additionalQuestions ? 'ico-minus' : 'ico-plus'" width="1.2rem" height="1.2rem" />
          </button>
        </div>
      </div>

      <div v-if="show.additionalQuestions" class="register-candidate-form__inset-block mb-4">
        <div class="register-candidate-form__additional-question-block">
          <div class="select__title">{{$tCandidateForm('The candidate looks like:')}}</div>
          <BaseRadioList
            v-model="formData.additionalQuestions.candidateLooksLike"
            vertical
            text-key="name"
            :options="dataLists.register_candidate_looks_like"
          />
        </div>
        <div class="register-candidate-form__additional-question-block">
          <div class="select__title">{{ $tCandidateForm('The candidate behaves:') }}</div>
          <BaseRadioList
            v-model="formData.additionalQuestions.candidateBehave"
            vertical
            text-key="name"
            :options="dataLists.register_candidate_behave"
          />
        </div>
        <div class="register-candidate-form__additional-question-block">
          <div class="select__title">{{ $tCandidateForm('During the interview with the candidate:') }}</div>
          <BaseRadioList
            v-model="formData.additionalQuestions.candidateFeelingAlcohol"
            vertical
            text-key="name"
            :options="dataLists.register_candidate_feeling_alcohol"
          />
        </div>
      </div>
    </RegisterCandidateFormBlock>

    <RegisterCandidateFormBlock>
      <BaseAlert v-if="show.registerError" :value="registerError.message" class="mb-4 _error" />
      <div class="register-candidate-form__register-btn-wrap">
        <button
          class="register-candidate-form__register-btn base-btn base-btn--green"
          :disabled="loading.form || !isValidPhone"
          @click="registerCandidate"
        >
          <SvgCircleLoader v-if="loading.form" size="18" />
          <span v-else>{{ $tCandidateForm('Register') }}</span>
        </button>
      </div>
    </RegisterCandidateFormBlock>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import RegisterCandidateFormBlock from './RegisterCandidateFormBlock';
import BirthdaySelect from '../BirthdaySelect';
import SCountry from '../loadingSelects/SCountry';
import SLanguage from '../loadingSelects/SLanguage';
import FilesUpload from '../FilesUploadMultiple';
import SProfession from '@/components/loadingSelects/SCategory'
import SvgCircleLoader from '../loaders/SvgCircleLoader';
import BaseInputError from '../BaseInputError';

const ANOTHER_DESIRED_CATEGORY_ID = 229;

function formDefaultWorkExperience() {
  return {
    specialization: null,
    term: null,
  };
}

function formDefaultLanguage() {
  return {
    value: null,
    experience: [],
  };
}

function formDefaultFile() {
  return {
    type: '',
    name: '',
    value: [],
  };
}

export default {
  name: 'RegisterCandidateForm',
  components: {
    BaseInputError,
    SvgCircleLoader,
    SProfession,
    FilesUpload,
    SLanguage,
    SCountry,
    BirthdaySelect,
    RegisterCandidateFormBlock,
  },
  props: {
    description: {
      type: String,
      default: Vue.prototype.$tCandidateForm('Hi, answer few questions') + ':',
    },
    useAdditionalQuestions: {
      type: Boolean,
      default: true,
    },
    customTitles: {
      type: Object,
      default: () => ({}),
    },
    registerCandidateFunction: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      show: {
        registerError: false,
        additionalQuestions: false,
      },

      loading: {
        form: false,
      },

      formData: {
        firstName: '',
        lastName: '',
        desiredCountries: [],
        genderOrPair: null,
        age: null,
        workType: null,
        desiredCategories: [],
        anotherDesiredCategory: '',
        workExperience: [
          formDefaultWorkExperience(),
        ],
        documents: {
          showed: null,
          files: [],
        },
        internationalPassport: null,
        workVisa: {
          value: null,
          country: null,
          expirationDate: new Date,
        },
        preferWorkBy: [],
        preferWorkBodyPositions: [],
        maxWorkWeight: null,
        readyToWork: null,
        languages: [
          formDefaultLanguage(),
        ],
        phone: null,

        additionalQuestions: {
          candidateLooksLike: null,
          candidateBehave: null,
          candidateFeelingAlcohol: null,
        },
      },

      registerError: null,
      validationErrors: {},
      isValidPhone: false,
    };
  },
  computed: {
    ...mapGetters({
      dataLists: 'candidate/register/formLists',
      isAgent: 'user/isAgent',
    }),
    anotherDesiredCategorySelected() {
      return this.isCheckedInFormList('desiredCategories', { id: ANOTHER_DESIRED_CATEGORY_ID });
    },
    haveWorkVisa() {
      return this.formData.workVisa.value?.id === 198;
    },
    haveCertificateDocuments() {
      return this.formData.documents.showed?.id !== 230;
    },
  },
  watch: {
    'dataLists.register_candidate_certificates_and_diplomas'(value) {
      this.formData.documents.showed = value.find(d => d.id === 230);
    },
    'dataLists.register_candidate_work_visa'(value) {
      this.formData.workVisa.value = value.find(d => d.id === 199);
    },
    haveWorkVisa(value) {
      const assignData = { country: null, expirationDate: null };
      if (value) {
        assignData.expirationDate = new Date();
      }
      Object.assign(this.formData.workVisa, assignData);
    },
    haveCertificateDocuments(value) {
      if (value) {
        this.formData.documents.files.push(formDefaultFile());
      }
      else {
        this.formData.documents.files = [];
      }
    },
  },
  beforeCreate() {
    this.$store.dispatch('candidate/register/updateFormLists');
  },
  created() {
    this.setQueryParamsData();
  },
  methods: {
    bindWithCheckboxesList(list, checked, value) {
      if (checked) list.push(value);
      else {
        const idx = list.findIndex(el => el.id === value.id);
        list.splice(idx, 1);
      }
    },
    isCheckedInFormList(listKey, value) {
      return this.isCheckedInCheckboxesList(this.formData[listKey], value);
    },
    isCheckedInCheckboxesList(list, value) {
      return !!list.find(el => el.id === value.id);
    },

    setQueryParamsData() {
      for (const [key, value] of Object.entries(this.$route.query)) {
        if (this.formData.hasOwnProperty(key)) this.formData[key] = value;
      }
    },

    onPhoneValidate(value) {
      this.isValidPhone = value.isValid;
    },

    deleteFormWorkExperience(idx) {
      this.formData.workExperience.splice(idx, 1);
    },
    addMoreWorkExperience() {
      this.formData.workExperience.push(formDefaultWorkExperience());
    },
    filterListLanguages(lang) {
      return !this.formData.languages.some(l => l.value?.id === lang.id);
    },
    addFormLanguage() {
      this.formData.languages.push(formDefaultLanguage());
    },
    deleteFormLanguage(idx) {
      this.formData.languages.splice(idx, 1);
    },
    addFormDocument() {
      this.formData.documents.files.push(formDefaultFile());
    },
    deleteFormDocument(idx) {
      this.formData.documents.files.splice(idx, 1);
    },

    async registerCandidate() {
      this.show.registerError = false;
      this.loading.form = true;

      try {
        if (this.registerCandidateFunction) {
          await this.registerCandidateFunction(this.formData);
        } else {
          await this.$store.dispatch('candidate/register/register', this.formData);
          this.$vModal.open('successfully-sent', { title: this.$tCandidateForm('Candidate successfully registered') });
          this.$router.push(`/account/${this.isAgent ? 'agent' : 'partner'}/resumes-base/resumes/`);
        }
      } catch (e) {
        this.showValidationErrors(e.response);
      } finally {
        this.loading.form = false;
      }
    },
    showValidationErrors(response) {
      this.registerError = response.data;
      this.show.registerError = true;

      if (response.data.errors && !isEmpty(response.data.errors)) {
        const { errors } = response.data;
        this.validationErrors = errors;

        const refsEntries = Object.entries(this.$refs);

        for (const [key, ref] of refsEntries) {
          if (!errors[key]) continue;

          ref.$el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
    },
  },
};
</script>
